import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Button,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";

const getPageRange = (current, total) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const currentNum = current + 1;
  const pages = [];
  if (currentNum <= 4) {
    for (let i = 1; i <= 5; i++) pages.push(i);
    pages.push("...");
    pages.push(total);
  } else if (currentNum >= total - 3) {
    pages.push(1);
    pages.push("...");
    for (let i = total - 4; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    pages.push("...");
    pages.push(currentNum - 1);
    pages.push(currentNum);
    pages.push(currentNum + 1);
    pages.push("...");
    pages.push(total);
  }
  return pages;
};

export default function DataTable({
  columns = [], // columns: { id, Header, accessor } accessor can be string key or function(row)
  rows = [],
  rowKey = (r) => r._id || r.id,
  pageSizeOptions = [10, 25, 50, 100],
  initialPageSize = 10,
  onRowClick = null,
  placeholder = "Search..."
}) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [q, setQ] = useState("");

  const getCell = (row, col) => {
    try {
      if (typeof col.accessor === "function") return col.accessor(row);
      if (typeof col.accessor === "string") return row[col.accessor];
      // fallback: try common keys by id
      const byId = row[col.id];
      return byId !== undefined && byId !== null ? byId : "";
    } catch {
      return "";
    }
  };

  const filtered = useMemo(() => {
    const query = (q || "").toString().trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) =>
      columns.some((c) => {
        const val = getCell(r, c);
        const strVal = val !== undefined && val !== null ? val : "";
        return strVal
          .toString()
          .toLowerCase()
          .includes(query);
      })
    );
  }, [rows, q, columns]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleRows = filtered.slice(currentPage * pageSize, currentPage * pageSize + pageSize);

  const startEntry = filtered.length === 0 ? 0 : currentPage * pageSize + 1;
  const endEntry = Math.min((currentPage + 1) * pageSize, filtered.length);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 16px rgba(11, 59, 96, 0.06)',
        overflow: 'hidden'
      }}
    >
      {/* Responsive Top Toolbar */}
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 1.75 },
          px: { xs: 1.75, sm: 2.5 },
          bgcolor: '#ffffff',
          borderBottom: '1px solid #f1f5f9'
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: '#0b3b60',
            letterSpacing: '-0.01em',
            fontSize: { xs: '0.95rem', sm: '1.05rem' }
          }}
        >
          Current Financial Members
        </Typography>

        <Box sx={{ flex: { sm: "0 1 420px" }, width: "100%", ml: { sm: 'auto' } }}>
          <TextField
            size="small"
            placeholder={placeholder}
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
            }}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#f8fafc',
                fontSize: '0.85rem'
              }
            }}
          />
        </Box>
      </Toolbar>

      {/* Main Table Container */}
      <TableContainer sx={{ maxHeight: 600, overflowX: "auto" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.id || col.Header}
                  sx={{
                    whiteSpace: "nowrap",
                    fontWeight: 700,
                    py: 1.2,
                    px: 1.5,
                    backgroundColor: '#0b3b60',
                    color: '#ffffff',
                    fontSize: '0.8rem',
                    letterSpacing: '0.02em',
                    borderBottom: '2px solid #072740',
                  }}
                >
                  {col.Header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, idx) => (
              <TableRow
                key={rowKey(row)}
                hover
                onClick={() => onRowClick && onRowClick(row)}
                sx={{
                  cursor: onRowClick ? "pointer" : "default",
                  backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: '#eef6fc !important',
                  }
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={(col.id || col.Header) + "_" + (rowKey(row) || "")}
                    sx={{ py: 1, px: 1.5, fontSize: '0.82rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}
                  >
                    {(() => {
                      const v = getCell(row, col);
                      return col.Cell ? col.Cell({ value: v, row }) : v;
                    })()}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>No records found</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>Try adjusting your search query</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Fully Responsive Pagination Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: { xs: 1.5, md: 2 },
          py: { xs: 1.5, sm: 1.75 },
          px: { xs: 1.5, sm: 2.5 },
          bgcolor: "#ffffff",
          borderTop: "1px solid #f1f5f9",
        }}
      >
        {/* Section 1: Entries Count & Rows Per Page */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", md: "flex-start" },
            gap: { xs: 1.5, sm: 2.5 },
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
          }}
        >
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: { xs: "0.78rem", sm: "0.82rem" } }}>
            Showing <strong style={{ color: "#0f172a" }}>{startEntry}</strong>–
            <strong style={{ color: "#0f172a" }}>{endEntry}</strong> of{" "}
            <strong style={{ color: "#0b3b60" }}>{filtered.length}</strong>
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.78rem" }}>
              Rows:
            </Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              sx={{
                height: 28,
                fontSize: "0.78rem",
                borderRadius: 1.5,
                bgcolor: "#f8fafc",
                "& .MuiSelect-select": { py: 0.3, px: 1 },
              }}
            >
              {pageSizeOptions.map((opt) => (
                <MenuItem key={opt} value={opt} sx={{ fontSize: "0.78rem" }}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Section 2: Navigation Buttons (Mobile-Adaptive Layout) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", md: "flex-end" },
            gap: { xs: 0.5, sm: 0.75 },
            width: { xs: "100%", md: "auto" },
          }}
        >
          {/* First Button (Icon-only on mobile, full button on desktop) */}
          <Tooltip title="First Page">
            <span>
              <IconButton
                size="small"
                onClick={() => setPage(0)}
                disabled={currentPage === 0}
                sx={{
                  display: { xs: "inline-flex", sm: "none" },
                  p: 0.6,
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  "&.Mui-disabled": { borderColor: "#e2e8f0" },
                }}
              >
                <FirstPageIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            size="small"
            variant="outlined"
            onClick={() => setPage(0)}
            disabled={currentPage === 0}
            startIcon={<FirstPageIcon />}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              py: 0.4,
              px: 1,
              borderColor: "#cbd5e1",
              color: "#334155",
              "&:disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            First
          </Button>

          {/* Previous Button */}
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            startIcon={<ChevronLeftIcon />}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.75rem", sm: "0.78rem" },
              py: 0.4,
              px: { xs: 1, sm: 1.3 },
              minWidth: { xs: 68, sm: "auto" },
              borderColor: "#cbd5e1",
              color: "#334155",
              "&:disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>Prev</Box>
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Previous</Box>
          </Button>

          {/* Page Numbers for Desktop (md+) */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5, mx: 0.5 }}>
            {getPageRange(currentPage, pageCount).map((p, idx) => {
              if (p === "...") {
                return (
                  <Typography key={`dots-${idx}`} variant="caption" sx={{ px: 0.5, color: "#94a3b8", fontWeight: 700 }}>
                    ...
                  </Typography>
                );
              }
              const isCurrent = p - 1 === currentPage;
              return (
                <Button
                  key={p}
                  size="small"
                  variant={isCurrent ? "contained" : "text"}
                  onClick={() => setPage(p - 1)}
                  sx={{
                    minWidth: 30,
                    height: 30,
                    p: 0,
                    borderRadius: "6px",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    backgroundColor: isCurrent ? "#0b3b60" : "transparent",
                    color: isCurrent ? "#ffffff" : "#334155",
                    "&:hover": {
                      backgroundColor: isCurrent ? "#072740" : "#f1f5f9",
                    },
                  }}
                >
                  {p}
                </Button>
              );
            })}
          </Box>

          {/* Mobile & Tablet Compact Page Badge */}
          <Chip
            label={`${currentPage + 1} / ${pageCount}`}
            size="small"
            sx={{
              display: { xs: "inline-flex", md: "none" },
              height: 28,
              px: 0.5,
              fontWeight: 700,
              fontSize: "0.75rem",
              backgroundColor: "rgba(11, 59, 96, 0.08)",
              color: "#0b3b60",
              borderRadius: "6px",
            }}
          />

          {/* Next Button */}
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPage(Math.min(pageCount - 1, currentPage + 1))}
            disabled={currentPage >= pageCount - 1}
            endIcon={<ChevronRightIcon />}
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.75rem", sm: "0.78rem" },
              py: 0.4,
              px: { xs: 1, sm: 1.3 },
              minWidth: { xs: 68, sm: "auto" },
              borderColor: "#cbd5e1",
              color: "#334155",
              "&:disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            Next
          </Button>

          {/* Last Button (Icon-only on mobile, full button on desktop) */}
          <Tooltip title="Last Page">
            <span>
              <IconButton
                size="small"
                onClick={() => setPage(pageCount - 1)}
                disabled={currentPage >= pageCount - 1}
                sx={{
                  display: { xs: "inline-flex", sm: "none" },
                  p: 0.6,
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  "&.Mui-disabled": { borderColor: "#e2e8f0" },
                }}
              >
                <LastPageIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            size="small"
            variant="outlined"
            onClick={() => setPage(pageCount - 1)}
            disabled={currentPage >= pageCount - 1}
            endIcon={<LastPageIcon />}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              py: 0.4,
              px: 1,
              borderColor: "#cbd5e1",
              color: "#334155",
              "&:disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
            }}
          >
            Last
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}